/**
 * ReviewAutomationCard - Google-Bewertungs-Automatik (global)
 * Auslöser: 60 Tage nach dem ersten Training (Standard) oder nach N Trainings.
 * Physio-Verordnung: zusätzlich nach der konfigurierten Sitzungszahl (Standard 9).
 * Kanäle: Mail + WhatsApp. Der Bewertungslink ist pro Studio
 * (studios.google_review_link) und wird beim Versand je Studio eingesetzt.
 */
import React, { useState, useEffect } from 'react';
import { Star, Send, Check, Mail, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewAutomationCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [templateStatus, setTemplateStatus] = useState(null);
  const [submittingTpl, setSubmittingTpl] = useState(false);
  const [checkingTpl, setCheckingTpl] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const loadTemplateStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/settings/google-review/template/status`, authHeaders());
      setTemplateStatus(res.data);
    } catch (e) {
      console.error('Error loading template status:', e);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/settings/google-review`, authHeaders());
        setSettings(res.data);
      } catch (e) {
        console.error('Error loading review settings:', e);
      }
    })();
    loadTemplateStatus();
  }, []);

  const handleSubmitTemplate = async () => {
    setSubmittingTpl(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/settings/google-review/template/submit`, {}, authHeaders());
      if (res.data.success) {
        toast.success(`Template "${res.data.template_name}" bei Meta eingereicht – Freigabe läuft.`);
        setSettings((s) => ({ ...s, whatsapp_template_name: res.data.template_name }));
        await loadTemplateStatus();
      } else {
        toast.error(res.data.error || 'Einreichen fehlgeschlagen (WhatsApp/Meta nicht konfiguriert?)');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Einreichen des Templates');
    } finally {
      setSubmittingTpl(false);
    }
  };

  const handleCheckTemplate = async () => {
    setCheckingTpl(true);
    await loadTemplateStatus();
    setCheckingTpl(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/settings/google-review`, {
        enabled: settings.enabled,
        trigger_mode: settings.trigger_mode || 'first_training_delay',
        delay_days: Number(settings.delay_days) || 60,
        after_trainings: Number(settings.after_trainings) || 3,
        physio_enabled: settings.physio_enabled,
        physio_after_sessions: Number(settings.physio_after_sessions) || 9,
        email_enabled: settings.email_enabled,
        whatsapp_enabled: settings.whatsapp_enabled,
        whatsapp_template_name: settings.whatsapp_template_name || '',
        whatsapp_language: settings.whatsapp_language || 'de',
        message_template: settings.message_template,
        email_subject: settings.email_subject,
        email_body: settings.email_body,
      }, authHeaders());
      setSettings((s) => ({ ...s, ...res.data }));
      toast.success('Bewertungs-Automatik gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/settings/google-review/run-now`, {}, authHeaders());
      const d = res.data;
      if (d.skipped === 'disabled') {
        toast.info('Automatik ist deaktiviert – zuerst aktivieren und speichern.');
      } else if (d.success) {
        toast.success(`${d.sent} Anfrage(n) gesendet (${d.skipped_no_link || 0} ohne Studio-Link, ${d.skipped_no_contact || 0} ohne Kontakt)`);
      } else {
        toast.error(d.error || 'Fehler beim Ausführen');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ausführen');
    } finally {
      setRunning(false);
    }
  };

  if (!settings) return null;

  const mode = settings.trigger_mode || 'first_training_delay';

  return (
    <Card className="mb-6 border-amber-200" data-testid="review-automation-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Google-Bewertungs-Automatik
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Sendet Kunden automatisch einmalig eine Bewertungsanfrage (Mail + WhatsApp)
              mit dem Google-Bewertungslink ihres Studios. Läuft täglich um 10:30 Uhr.
              Bisher gesendet: <strong>{settings.total_sent || 0}</strong>
            </p>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(checked) => set({ enabled: !!checked })}
            data-testid="review-automation-toggle"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Auslöser */}
        <div className="space-y-2">
          <Label>Wann soll die Anfrage raus?</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              data-testid="review-mode-delay"
              onClick={() => set({ trigger_mode: 'first_training_delay' })}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${mode === 'first_training_delay' ? 'border-amber-500 bg-amber-50 font-medium' : 'border-gray-200 hover:border-amber-300'}`}
            >
              Nach dem ersten Training (Zeitverzögert)
            </button>
            <button
              type="button"
              data-testid="review-mode-count"
              onClick={() => set({ trigger_mode: 'after_trainings' })}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${mode === 'after_trainings' ? 'border-amber-500 bg-amber-50 font-medium' : 'border-gray-200 hover:border-amber-300'}`}
            >
              Nach einer Anzahl Trainings
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mode === 'first_training_delay' ? (
            <div className="space-y-2">
              <Label htmlFor="delay_days">Tage nach dem ersten Training</Label>
              <Input
                id="delay_days"
                data-testid="review-delay-days"
                type="number"
                min="0"
                value={settings.delay_days ?? 60}
                onChange={(e) => set({ delay_days: e.target.value })}
                className="w-32"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="after_trainings">Anfrage nach ... Trainings</Label>
              <Input
                id="after_trainings"
                data-testid="review-automation-threshold"
                type="number"
                min="1"
                value={settings.after_trainings ?? 3}
                onChange={(e) => set({ after_trainings: e.target.value })}
                className="w-32"
              />
            </div>
          )}
        </div>

        {/* Physio */}
        <div className="rounded-lg border border-gray-200 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Physio-Verordnung</Label>
              <p className="text-xs text-gray-500 mt-0.5">
                Patienten mit Verordnung erhalten die Anfrage nach der eingestellten Sitzungszahl.
              </p>
            </div>
            <Switch
              checked={settings.physio_enabled !== false}
              onCheckedChange={(checked) => set({ physio_enabled: !!checked })}
              data-testid="review-physio-toggle"
            />
          </div>
          {settings.physio_enabled !== false && (
            <div className="space-y-2">
              <Label htmlFor="physio_after_sessions">Nach ... abgeschlossenen Sitzungen</Label>
              <Input
                id="physio_after_sessions"
                data-testid="review-physio-sessions"
                type="number"
                min="1"
                value={settings.physio_after_sessions ?? 9}
                onChange={(e) => set({ physio_after_sessions: e.target.value })}
                className="w-32"
              />
            </div>
          )}
        </div>

        {/* Kanäle */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
            <span className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-gray-500" /> E-Mail senden</span>
            <Switch
              checked={settings.email_enabled !== false}
              onCheckedChange={(checked) => set({ email_enabled: !!checked })}
              data-testid="review-channel-email"
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
            <span className="flex items-center gap-2 text-sm"><MessageCircle className="h-4 w-4 text-gray-500" /> WhatsApp senden</span>
            <Switch
              checked={settings.whatsapp_enabled !== false}
              onCheckedChange={(checked) => set({ whatsapp_enabled: !!checked })}
              data-testid="review-channel-whatsapp"
            />
          </div>
        </div>

        {/* E-Mail-Vorlage */}
        {settings.email_enabled !== false && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="email_subject">E-Mail-Betreff</Label>
              <Input
                id="email_subject"
                data-testid="review-email-subject"
                value={settings.email_subject || ''}
                onChange={(e) => set({ email_subject: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email_body">E-Mail-Text</Label>
              <Textarea
                id="email_body"
                data-testid="review-email-body"
                rows={6}
                value={settings.email_body || ''}
                onChange={(e) => set({ email_body: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* WhatsApp-Vorlage */}
        {settings.whatsapp_enabled !== false && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="whatsapp_template_name">WhatsApp-Template-Name (Meta, freigegeben)</Label>
              <Input
                id="whatsapp_template_name"
                data-testid="review-wa-template-name"
                placeholder="z. B. google_bewertung_v1 (leer = Klartext, nur im 24h-Fenster)"
                value={settings.whatsapp_template_name || ''}
                onChange={(e) => set({ whatsapp_template_name: e.target.value })}
              />
              <p className="text-xs text-gray-500">
                Für den Versand nach 60 Tagen verlangt Meta ein freigegebenes Template.
                Ohne Template wird ein Klartext-Versuch gemacht (nur zustellbar, wenn der Kunde
                in den letzten 24 h geschrieben hat).
              </p>
              {/* Meta-Template einreichen & Status */}
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="review-wa-template-box">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-700">
                    Google-Bewertungs-Template bei Meta
                  </span>
                  {templateStatus && (() => {
                    const s = templateStatus.status;
                    const map = {
                      APPROVED: { t: 'Freigegeben', c: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                      PENDING: { t: 'In Prüfung bei Meta', c: 'bg-amber-100 text-amber-700 border-amber-300' },
                      REJECTED: { t: 'Abgelehnt', c: 'bg-red-100 text-red-700 border-red-300' },
                      NOT_FOUND: { t: 'Noch nicht eingereicht', c: 'bg-gray-100 text-gray-600 border-gray-300' },
                    };
                    const v = map[s] || { t: s, c: 'bg-gray-100 text-gray-600 border-gray-300' };
                    return (
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${v.c}`} data-testid="review-wa-template-status">
                        {v.t}
                      </span>
                    );
                  })()}
                </div>
                <p className="text-xs text-gray-500">
                  Reicht ein vorbereitetes Template bei Meta ein. Die Freigabe übernimmt Meta
                  (dauert meist einige Minuten bis Stunden). Erst nach Freigabe wird nach 60 Tagen
                  automatisch per WhatsApp zugestellt.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleSubmitTemplate}
                    disabled={submittingTpl || templateStatus?.status === 'APPROVED' || templateStatus?.status === 'PENDING'}
                    data-testid="review-wa-template-submit-btn"
                  >
                    {submittingTpl ? 'Wird eingereicht...' : 'Template bei Meta einreichen'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={handleCheckTemplate}
                    disabled={checkingTpl}
                    data-testid="review-wa-template-refresh-btn"
                  >
                    {checkingTpl ? 'Prüft...' : 'Status aktualisieren'}
                  </Button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message_template">WhatsApp-Nachricht (Klartext-Fallback)</Label>
              <Textarea
                id="message_template"
                data-testid="review-automation-template"
                rows={5}
                value={settings.message_template || ''}
                onChange={(e) => set({ message_template: e.target.value })}
              />
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Platzhalter: {'{first_name}'}, {'{training_count}'}, {'{studio_name}'}, {'{review_link}'}
        </p>

        <div className="flex items-center gap-2 pt-1">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="review-automation-save-btn"
          >
            <Check className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
          <Button
            variant="outline"
            onClick={handleRunNow}
            disabled={running}
            data-testid="review-automation-run-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            {running ? 'Läuft...' : 'Jetzt prüfen & senden'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReviewAutomationCard;
